import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getProjects, setProjects } from 'services'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { IProjectTemplate } from '.'
import { defaultInputs } from './constants'

interface ProjectDetailsProps {
  defaults: IProjectTemplate
  onBack: Function
  onComplete: Function
}

export function ProjectDetails(props: ProjectDetailsProps) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const { defaults } = props

  useEffect(() => {
    onUpdateDefaults()
  }, [defaults])

  const onUpdateDefaults = async () => {
    if (!defaults) {
      setInputs(defaultInputs())
      return
    }
    const newStats = cloneDeep(defaultInputs())
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key]
    }
    setInputs(newStats)
  }

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: number) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate(newInputs[key])
    setInputs(newInputs)
  }

  const onSubmit = async () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true

      data[key] = value
    }

    if (hasError) {
      setInputs(newStats)
      return
    }

    setLoading(true)

    const existingProjects: IProjectTemplate[] = await getProjects()
    const isDuplicate =
      existingProjects?.length > 0 &&
      existingProjects.some((item: IProjectTemplate) => {
        return item.id !== defaults.id && item.name.toLowerCase() === data.name.toLowerCase()
      })

    if (isDuplicate) {
      toast('Same Project Template already exists.', { type: 'error' })
      setLoading(false)
      return
    }

    const id = defaults.id
    const index = existingProjects.findIndex((v) => v.id === id)
    existingProjects[index].name = data.name
    await setProjects(existingProjects)
    toast('Project Template updated successfully.', { type: 'success' })
    setLoading(false)
    onBack()
  }

  return (
    <div className="email-template-container max-w-[720px]">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Update Project Template
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Project Template</p>
        </div>
      </Button>
      <div className="mb-3">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]

          return (
            <div
              className={`mb-4 input ${input.span ? 'col-span-1 lg:col-span-4 md:col-span-3 sm:col-span-2' : ''}`}
              key={index}
            >
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>

      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'}</>
        </Button>
      </div>
    </div>
  )
}
